import { useEffect, useRef } from 'react';
import { useBodyScrollLock } from '../Hooks/useBodyScrollLock';

/**
 * Shared bookkeeping for full-screen overlays (Modal, Drawer, the download
 * preview, the dashboard drill-down).
 *
 * Each of these used to own two `document`-level side effects privately, which
 * broke as soon as two of them were open at once:
 *
 * - Each registered its own Escape listener, so Escape closed *every* open
 *   overlay — including the one underneath the overlay the user was looking at.
 * - Each wrote `document.body.style.overflow` with no refcount, so closing the
 *   inner overlay unlocked scrolling behind the outer one that was still open.
 *
 * Both now go through this module: Escape is routed to the topmost layer only,
 * and every layer holds the one refcounted body lock in `useBodyScrollLock`, so
 * the body is unlocked exactly once, when the last holder closes. The counter
 * lives there rather than here on purpose — a second counter writing the same
 * style would stomp the first, which is the bug being fixed.
 *
 * Popovers (`ui/DropdownMenu`, `ui/Select`) deliberately stay out of this — they
 * sit above overlays at `z-[30000]`, close themselves, and keep their own
 * listeners. This module never calls `stopPropagation`, so their Escape handling
 * is unaffected. The flip side is that the capture-phase listener below cannot
 * be suppressed by a descendant: a component that needs Escape scoped to itself
 * would need an opt-out added here rather than a local `stopPropagation`.
 */

type OverlayLayer = { onEscape: () => void };

const layers: OverlayLayer[] = [];

let escapeListenerBound = false;

const handleEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || layers.length === 0) return;
  // Only the topmost layer reacts. A layer with no handler (a `persistent`
  // Modal) still shields the layers beneath it rather than falling through.
  //
  // "Topmost" means most recently registered. React runs effects child-first,
  // so two overlays that become active in the *same* commit register inner
  // before outer — the reverse of paint order. Every overlay pairing in the app
  // today activates sequentially (a modal is already open before a preview is
  // opened from it), which registers in the right order. A future overlay that
  // mounts already-nested in one commit would need an explicit z-order here.
  layers[layers.length - 1].onEscape();
};

const bindEscapeListener = () => {
  if (escapeListenerBound) return;
  // Capture phase, so a child that stops the event bubbling can't strand the
  // topmost overlay. We never stop propagation ourselves.
  document.addEventListener('keydown', handleEscape, true);
  escapeListenerBound = true;
};

const unbindEscapeListenerIfIdle = () => {
  if (!escapeListenerBound || layers.length > 0) return;
  document.removeEventListener('keydown', handleEscape, true);
  escapeListenerBound = false;
};

type UseOverlayLayerOptions = {
  /** Whether the overlay is currently on screen. */
  active: boolean;
  /**
   * Called when Escape is pressed *and* this is the topmost layer. Omit it for
   * an overlay that shouldn't close on Escape (a `persistent` Modal) — it still
   * takes its place in the stack and shields the layers beneath it.
   */
  onEscape?: () => void;
};

/**
 * Registers an overlay as a layer for as long as it is `active`, holding the
 * shared body scroll lock for that time.
 *
 * `onEscape` is read through a ref, so passing a fresh closure on every render
 * (the common case) doesn't re-register the layer or churn the scroll lock.
 */
export function useOverlayLayer({ active, onEscape }: UseOverlayLayerOptions) {
  const onEscapeRef = useRef(onEscape);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useEffect(() => {
    if (!active) return;

    const layer: OverlayLayer = { onEscape: () => onEscapeRef.current?.() };
    layers.push(layer);
    bindEscapeListener();

    return () => {
      const index = layers.lastIndexOf(layer);
      if (index !== -1) layers.splice(index, 1);
      unbindEscapeListenerIfIdle();
    };
  }, [active]);

  useBodyScrollLock(active);
}

/**
 * Drops the layer stack and its listener. Tests only — the stack is
 * process-wide, so a test that leaves a layer registered would otherwise leak
 * into the next one. The scroll lock's counter is deliberately out of reach;
 * unmounting (RTL's automatic cleanup) is what releases it.
 */
export function resetOverlayStackForTests() {
  layers.length = 0;
  if (escapeListenerBound) {
    document.removeEventListener('keydown', handleEscape, true);
    escapeListenerBound = false;
  }
}
